import { ChevronRight } from "lucide-react";
import { Button, Text } from "@ferrivia/ui";
import { useTranslation } from "@ferrivia/i18n";
import { useAnswerText, useQuestionnaireCopy } from "@ferrivia/questionnaire-ui";
import {
  parsePhotoIds,
  reachableGroupFields,
  type ItemFieldMap,
  type RepeatingQuestionDefinition,
} from "@ferrivia/api-contract";
import { ITEM_FIELD_KEY_SEPARATOR } from "./GroupItemFields";
import s from "./RepeatingGroup.module.css";

export interface RepeatingGroupProps {
  group: RepeatingQuestionDefinition;
  /** In definition order, i.e. by item index ascending -- but the index
      itself is carried separately in `itemIndexes` (same position), not
      re-derived from array position: once an item has been removed, later
      items keep their original wire `itemIndex` rather than shifting down
      (see apps/api's `deleteAnswerItem`). */
  items: readonly ItemFieldMap[];
  itemIndexes: readonly number[];
  /** `useQuestionnaire`'s `invalidKeys`, keyed by its `itemFieldKey` -- a
      card whose item has any of them asks to be checked. */
  invalidKeys: ReadonlySet<string>;
  /** `null` opens the editor on a new item. */
  onOpenEditor(itemIndex: number | null): void;
}

/** One repeating-group question's items (`iron_supplements`,
    `other_supplement_items`, `iron_infusion_items`) as a list: the group's
    label/intro, one summary card per saved item, and an "Lisää" button.
    Adding and editing both happen in the overlay's full-screen
    `ItemEditorScreen`, so nothing here writes an answer.

    A card shows the group's `summaryFieldKeys` values as entered, formatted
    by `useAnswerText` -- never derived from or ranked by them. */
export function RepeatingGroup({ group, items, itemIndexes, invalidKeys, onOpenEditor }: RepeatingGroupProps) {
  const { t } = useTranslation("client");
  const copy = useQuestionnaireCopy();
  const answerText = useAnswerText();
  const groupCopy = copy.groupFor(group.key);
  const [titleKey, ...summarySubtitleKeys] = group.summaryFieldKeys ?? [];
  // A photo count ("2 kuvaa") joins the subtitle, so an item with only photos
  // still says what it holds.
  const photoFieldKeys = group.fields.filter((field) => field.control === "photo_upload").map((field) => field.key);
  const subtitleKeys = [...summarySubtitleKeys, ...photoFieldKeys];

  function summaryPart(item: ItemFieldMap, reachable: ReadonlySet<string>, fieldKey: string | undefined) {
    if (!fieldKey || !reachable.has(fieldKey)) return undefined;
    const field = group.fields.find((candidate) => candidate.key === fieldKey);
    const value = item.get(fieldKey);
    if (!field || !value) return undefined;
    if (field.control === "photo_upload" && value.kind === "text" && parsePhotoIds(value.value).length === 0) {
      return undefined;
    }
    const text = answerText.forGroupField(group.key, field, value);
    return text === "" ? undefined : text;
  }

  return (
    <div className={s.root}>
      <Text as="h2" size="lg" weight="bold" className={s.label}>
        {groupCopy?.label ?? group.key}
      </Text>
      {groupCopy?.intro ? (
        <Text as="p" size="sm" className={s.intro}>
          {groupCopy.intro}
        </Text>
      ) : null}

      {items.length > 0 ? (
        <ul className={s.list}>
          {items.map((item, position) => {
            const itemIndex = itemIndexes[position];
            const reachable = new Set(reachableGroupFields(group, item).map((field) => field.key));
            const title =
              summaryPart(item, reachable, titleKey) ??
              t("questionnaire.itemNumber", "Kohta {{number}}", { number: position + 1 });
            const subtitle = subtitleKeys
              .map((key) => summaryPart(item, reachable, key))
              .filter((part): part is string => part !== undefined)
              .join(" · ");
            const prefix = [group.key, itemIndex, ""].join(ITEM_FIELD_KEY_SEPARATOR);
            const needsCheck = [...invalidKeys].some((key) => key.startsWith(prefix));
            return (
              <li key={itemIndex}>
                <button
                  type="button"
                  className={s.card}
                  onClick={() => onOpenEditor(itemIndex)}
                  aria-label={`${title}, ${t("questionnaire.editItem", "muokkaa")}`}
                >
                  <span className={s.cardText}>
                    <span className={s.cardTitle}>{title}</span>
                    {subtitle ? <span className={s.cardSubtitle}>{subtitle}</span> : null}
                    {needsCheck ? (
                      <span className={s.error}>{t("questionnaire.itemNeedsCheck", "Tarkista tiedot")}</span>
                    ) : null}
                  </span>
                  <ChevronRight aria-hidden size={18} className={s.cardChevron} />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      <Button type="button" variant="secondary" onClick={() => onOpenEditor(null)} className={s.addButton}>
        {groupCopy?.addLabel ?? t("questionnaire.addItem", "+ Lisää")}
      </Button>
    </div>
  );
}
